import { useState } from 'react';

export default function ContactForm({ defaultSubject = '', theme = 'light' }) {
  const isLight = theme === 'light';

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: defaultSubject ? `Hej Michael,\n\nJeg henvender mig vedrørende ${defaultSubject}: ` : '',
    website_url: '', // Honeypot field (must remain empty for humans)
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = 'Venligst indtast dit fulde navn';
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast et telefonnummer';
    } else if (!/^[\d\s+()\-]{7,20}$/.test(formData.phone.trim())) {
      errs.phone = 'Indtast et gyldigt telefonnummer (min. 8 cifre)';
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din emailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Indtast en gyldig emailadresse';
    }
    if (!formData.message.trim() || formData.message.trim().length < 8) {
      errs.message = 'Skriv venligst en kort beskrivelse af din forespørgsel (min. 8 tegn)';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
        website_url: '',
      });
    } catch (err) {
      console.error('Submission failed:', err);
      setSubmitStatus('error');
      setErrorMessage(
        err.message || 'Der opstod en fejl ved afsendelse. Du er altid velkommen til at ringe direkte på 26 36 83 06.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`rounded-none p-6 sm:p-8 md:p-10 shadow-xl transition-colors ${
        isLight
          ? 'bg-white border border-[#DDD7CE] text-[#141416]'
          : 'bg-[#121215] border border-white/10 text-[#F4EFEA]'
      }`}
    >
      {submitStatus === 'success' ? (
        <div className="py-8 text-center space-y-4 animate-in fade-in duration-300">
          <div
            className={`w-16 h-16 rounded-none flex items-center justify-center mx-auto ${
              isLight
                ? 'bg-black/5 border border-black/20 text-[#141416]'
                : 'bg-white/10 border border-white/20 text-white'
            }`}
          >
            <i className="fa-solid fa-check text-2xl" aria-hidden="true" />
          </div>
          <h3
            className={`text-2xl font-serif ${
              isLight ? 'text-[#141416]' : 'text-[#F4EFEA]'
            }`}
          >
            Mange tak for din besked!
          </h3>
          <p
            className={`text-sm max-w-md mx-auto leading-relaxed ${
              isLight ? 'text-zinc-600' : 'text-zinc-400'
            }`}
          >
            Jeg har modtaget din forespørgsel og vender tilbage til dig hurtigst muligt – typisk inden for 24 timer.
          </p>
          <div className="pt-4">
            <button
              type="button"
              onClick={() => setSubmitStatus(null)}
              className={`text-xs uppercase tracking-widest font-semibold hover:underline ${
                isLight ? 'text-[#141416]' : 'text-white'
              }`}
            >
              Send endnu en besked
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Honeypot field (Strictly invisible to humans) */}
          <div className="sr-only absolute -top-9999px left-0" aria-hidden="true">
            <label htmlFor="website_url">Website</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Navn */}
            <div>
              <label
                htmlFor="name"
                className={`block text-xs uppercase tracking-wider font-semibold mb-2 ${
                  isLight ? 'text-[#141416]' : 'text-zinc-300'
                }`}
              >
                Dit Navn <span className={isLight ? 'text-[#141416]' : 'text-white'}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Mette & Frederik"
                className={`w-full px-0 py-3 rounded-none border-0 border-b bg-transparent text-sm transition-colors focus:outline-none ${
                  isLight
                    ? `text-[#141416] placeholder-zinc-400 ${
                        errors.name ? 'border-red-500' : 'border-zinc-300 focus:border-black'
                      }`
                    : `text-[#F4EFEA] placeholder-zinc-500 ${
                        errors.name ? 'border-red-500/80' : 'border-white/20 focus:border-white'
                      }`
                }`}
                required
              />
              {errors.name && (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Telefonnummer */}
            <div>
              <label
                htmlFor="phone"
                className={`block text-xs uppercase tracking-wider font-semibold mb-2 ${
                  isLight ? 'text-[#141416]' : 'text-zinc-300'
                }`}
              >
                Telefonnummer <span className={isLight ? 'text-[#141416]' : 'text-white'}>*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="26 36 83 06"
                className={`w-full px-0 py-3 rounded-none border-0 border-b bg-transparent text-sm transition-colors focus:outline-none ${
                  isLight
                    ? `text-[#141416] placeholder-zinc-400 ${
                        errors.phone ? 'border-red-500' : 'border-zinc-300 focus:border-black'
                      }`
                    : `text-[#F4EFEA] placeholder-zinc-500 ${
                        errors.phone ? 'border-red-500/80' : 'border-white/20 focus:border-white'
                      }`
                }`}
                required
              />
              {errors.phone && (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                  <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                  {errors.phone}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className={`block text-xs uppercase tracking-wider font-semibold mb-2 ${
                isLight ? 'text-[#141416]' : 'text-zinc-300'
              }`}
            >
              Emailadresse <span className={isLight ? 'text-[#141416]' : 'text-white'}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="din-email@eksempel.dk"
              className={`w-full px-0 py-3 rounded-none border-0 border-b bg-transparent text-sm transition-colors focus:outline-none ${
                isLight
                  ? `text-[#141416] placeholder-zinc-400 ${
                      errors.email ? 'border-red-500' : 'border-zinc-300 focus:border-black'
                    }`
                  : `text-[#F4EFEA] placeholder-zinc-500 ${
                      errors.email ? 'border-red-500/80' : 'border-white/20 focus:border-white'
                    }`
              }`}
              required
            />
            {errors.email && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Beskrivelse */}
          <div>
            <label
              htmlFor="message"
              className={`block text-xs uppercase tracking-wider font-semibold mb-2 ${
                isLight ? 'text-[#141416]' : 'text-zinc-300'
              }`}
            >
              Beskrivelse af opgave / arrangement <span className={isLight ? 'text-[#141416]' : 'text-white'}>*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Fortæl gerne lidt om dato, lokation, antal timer, type opgave (foto/video) og jeres ønsker..."
              className={`w-full px-0 py-3 rounded-none border-0 border-b bg-transparent text-sm transition-colors focus:outline-none resize-y ${
                isLight
                  ? `text-[#141416] placeholder-zinc-400 ${
                      errors.message ? 'border-red-500' : 'border-zinc-300 focus:border-black'
                    }`
                  : `text-[#F4EFEA] placeholder-zinc-500 ${
                      errors.message ? 'border-red-500/80' : 'border-white/20 focus:border-white'
                    }`
              }`}
              required
            />
            {errors.message && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" aria-hidden="true" />
                {errors.message}
              </p>
            )}
          </div>

          {submitStatus === 'error' && (
            <div className="p-4 rounded-none bg-red-500/10 border border-red-500/30 text-red-600 text-xs">
              {errorMessage}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className={`cta-btn w-full inline-flex items-center justify-center gap-2 py-4 px-8 font-semibold text-base tracking-wide transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              isLight
                ? 'bg-[#141416] hover:bg-black text-white'
                : 'bg-white hover:bg-stone-100 text-[#0C0C0D]'
            }`}
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-circle-notch animate-spin text-sm" aria-hidden="true" />
                <span>Sender din forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send uforpligtende forespørgsel</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
